import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { debounce, get } from "lodash";
import {
  GETPATIENTS,
  RESET,
} from "./../../../services/redux/slices/assets/persons/users";
import { MDBAnimation, MDBIcon, MDBProgress } from "mdbreact";
import {
  formatNameToObj,
  fullName,
  getAge,
  getGenderIcon,
} from "./../../../services/utilities";
import Notification from "./notification";
import "../style.css";
/**
 * A Search component that allows the user to search for a patient by last name, first name, and middle name.
 * The component will make an API call to search for patients and render a list of results below the search input.
 * The user can select a patient from the list and the setPatient callback will be called with the selected patient.
 * The component also renders a button to register a new patient if no patient record is found with the search key.
 * The setSearchKey callback will be called with the search key when the button is clicked.
 *
 * @param {function} setPatient - A callback function that will be called when a patient is selected from the list.
 * @param {function} setRegister - A callback function that will be called when the button to register a new patient is clicked.
 *
 * @returns {JSX.Element} users
 */
export default function Search({
  setPatient = () => {},
  setRegister = () => {},
  excludes = [],
  excludeKey = "",
  notFoundMessage = "No Patient Record found.",
}) {
  const { filtered } = useSelector(({ users }) => users),
    { token } = useSelector((state) => state.auth),
    [patients, setPatients] = useState([]),
    [didSearch, setDidSearch] = useState(false),
    [didHover, setDidHover] = useState(false),
    [isFetching, setIsFetching] = useState(false),
    [searchKey, setSearchKey] = useState(""),
    dispatch = useDispatch();

  useEffect(() => {
    let _filtered = filtered;
    if (excludes.length > 0) {
      _filtered = filtered.filter((item) => {
        return !excludes.some((exclude) => {
          const value = get(exclude, excludeKey);
          return String(value) === String(item._id);
        });
      });
    }
    setPatients(_filtered);
  }, [filtered, excludes, excludeKey]);
  /**
   * This function is debounced which means it will only be executed after 1000 milliseconds (1 second)
   * of not being called again. This is useful for when the user is typing in the search
   * input quickly and we don't want to make multiple API calls to search for the patient
   * for each keystroke.
  
   * The function takes a search key as an argument which is the value of the search
   * input. The key is then formatted into an object with last name, first name, and
   * middle name as separate properties. This is because the API endpoint for searching
   * for patients expects the search key to be an object with these properties.
  
   * The function then dispatches the GETPATIENTS action with the token and the formatted
   * search key as arguments. The GETPATIENTS action will make the API call to search
   * for patients and update the state with the result.
   */
  const debouncedSearch = useMemo(
    () =>
      debounce((searchKey) => {
        const key = formatNameToObj(searchKey);
        dispatch(GETPATIENTS({ token, key })).then(() => {
          setIsFetching(false);
        });
      }, 1000),
    [token, dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChange = (e) => {
    const _searchKey = e.target.value;
    setSearchKey(_searchKey);
    const searchKey = _searchKey.split(",");
    if (searchKey.length > 1 && searchKey[1].trim()) {
      setIsFetching(true);
      setDidSearch(true);
      return debouncedSearch(_searchKey);
    }
  };

  const handleSelect = (user) => {
    setPatient(user);
    setSearchKey("");
    dispatch(RESET());
    setDidSearch(false);
  };

  const handleRegister = () => {
    setRegister(formatNameToObj(searchKey));
    setSearchKey("");
    dispatch(RESET());
    setDidSearch(false);
  };

  return (
    <div className="d-flex align-items-center" style={{ position: "relative" }}>
      <Notification didSearch={didSearch} />
      <div className={`searchable-search ${didSearch && "active"}`}>
        <div className="searchable-search-suggestions">
          {!isFetching ? (
            <>
              {!patients.length ? (
                <small
                  className={didHover ? "text-primary" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={handleRegister}
                  onMouseEnter={() => setDidHover(true)}
                  onMouseLeave={() => setDidHover(false)}
                >
                  {notFoundMessage} <br /> click here to register
                </small>
              ) : (
                <ul>
                  {patients?.map((user) => {
                    // console.log(user);
                    const { _id, fullName: fullname } = user;

                    return (
                      <li
                        onClick={() => handleSelect(user)}
                        key={_id}
                        className="text-dark text-nowrap"
                      >
                        {getGenderIcon(user.gender)} {fullName(fullname)} |{" "}
                        {getAge(user.dob)}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : (
            <>
              {new Array(5).fill("").map((_, index) => (
                <MDBAnimation
                  key={index}
                  className="p-1 ml-2 mr-2 mt-1"
                  type="flash"
                  infinite
                  delay={`${index + 1}00ms`}
                  duration="3000ms"
                >
                  <MDBProgress
                    animated
                    color="light"
                    value={3000}
                    id="progress-table"
                  ></MDBProgress>
                </MDBAnimation>
              ))}
            </>
          )}
        </div>
        <input
          value={searchKey}
          onChange={handleChange}
          placeholder="Search..."
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          type="submit"
          style={{
            color: "white",
          }}
          disabled={isFetching}
          className={didSearch && !isFetching ? "bg-danger" : ""}
          onClick={
            didSearch
              ? () => {
                  setDidSearch(false);
                  setSearchKey("");
                }
              : () => console.log("search")
          }
        >
          <MDBIcon
            icon={didSearch && !isFetching ? "times" : "search"}
            className="search-icon"
          />
        </button>
      </div>
    </div>
  );
}
